/**
 * Content Writer / Senior Content Writer Workspace — pure presentation logic
 * (Task 219, backed by the live Task 220 backend). No React, no fetch.
 *
 * The writer's deliverable is IN-SYSTEM TEXT (Task 220 `content_writer_drafts`),
 * not a file upload — so the lanes are DRAFT-state-driven (not the task-status
 * lanes the Designer/Video workspaces use). The writer authors per content
 * VARIANT; a content TASK (content_item) groups its variants' drafts.
 *
 * Assigned-only: the queue is derived from `list-my-production-tasks` (RLS
 * assignee-match) + the caller's own `list-content-writer-drafts`. No org-wide
 * reader, no `useClientScope`.
 */

import type {
  ClientAssignmentSummary,
  ContentDiscipline,
  ContentOrigin,
  ContentVariantPlatform,
  ContentVariantPlatformSpecific,
  ContentWriterDraftRow,
  ContentWriterDraftStatus,
  GenerateWriterAiDraftsData,
  MyProductionTask,
  MyProductionTaskStatus,
  SaveContentCalendarDraftItemInput,
} from '../api/types';
import { BRIEF_LABELS_AR, isVideoBriefPlatform, readBriefField } from './client/productionBrief';
// `platformLabelAr` is the role-neutral platform label (lives in the designer
// logic, re-exported by the shared production-workspace facade).
import { platformLabelAr } from './productionWorkspace.logic';

export { platformLabelAr };

// ── Draft-state lanes ────────────────────────────────────────────────
export type ContentLaneKey =
  | 'not_started'
  | 'drafting'
  | 'returned'
  | 'submitted'
  | 'approved'
  | 'blocked';

export interface ContentLane {
  readonly key: ContentLaneKey;
  readonly label: string;
  readonly deemphasized: boolean;
}
export const CONTENT_LANES: ReadonlyArray<ContentLane> = [
  { key: 'returned', label: 'مُعادة بملاحظات', deemphasized: false },
  { key: 'not_started', label: 'لم تبدأ', deemphasized: false },
  { key: 'drafting', label: 'قيد الكتابة', deemphasized: false },
  { key: 'submitted', label: 'بانتظار مراجعة كبير الكتّاب', deemphasized: true },
  { key: 'approved', label: 'معتمدة / جاهزة', deemphasized: true },
  { key: 'blocked', label: 'محظورة / بانتظار معلومات', deemphasized: true },
];

export function draftStatusLabelAr(status: ContentWriterDraftStatus): string {
  switch (status) {
    case 'drafting':
      return 'قيد الكتابة';
    case 'submitted_to_senior':
      return 'بانتظار مراجعة كبير الكتّاب';
    case 'returned_with_notes':
      return 'مُعادة بملاحظات';
    case 'approved':
      return 'معتمدة';
    default:
      return status;
  }
}

export function disciplineLabelAr(discipline: ContentDiscipline): string {
  switch (discipline) {
    case 'design':
      return 'تصميم';
    case 'video':
      return 'فيديو';
    case 'content':
      return 'محتوى';
    case 'writing':
      return 'كتابة';
    default:
      return discipline;
  }
}

/**
 * Task 292C1 — the writer queue lists ONLY the caller's writing tasks.
 *
 * The item's discipline is NOT the discriminator. `content_items.discipline`
 * is immutable by trigger and stays `content` for the whole writing lane, so
 * filtering on it would also let a post-approval PRODUCTION task on the same
 * item into the writer's queue. The writing task is discriminated by its own
 * `metadata.discipline`, which reaches the FE through
 * `assignment_metadata` (`send_content_item_to_writing_v1` stamps it into
 * task_assignments.metadata precisely so this needs no new wire field).
 *
 * Fails CLOSED: an assignment with no readable discipline is not a writing
 * task and is excluded.
 */
export function writingTasksOnly(
  tasks: ReadonlyArray<MyProductionTask>,
): ReadonlyArray<MyProductionTask> {
  return tasks.filter(isWritingTask);
}

/**
 * Task 292C1-QA-A — the ONE predicate both the writer queue and «مهامي» use to
 * tell a writing task from a production task. Exported so the two surfaces
 * cannot drift: My Tasks labels and links a task as "writing" on exactly the
 * same basis the writer workspace admits it.
 *
 * Deliberately NOT `content_item.discipline` — that stays `content` for the
 * whole writing lane (the column is immutable by trigger), which is why My
 * Tasks used to render «محتوى» for a writing task.
 */
export function isWritingTask(task: MyProductionTask): boolean {
  return assignmentDiscipline(task) === 'writing';
}

/**
 * Task 292C1-QA-A (Codex round 1, P1) — should a URL-driven task switch stop to
 * confirm first?
 *
 * The route effect fires for browser BACK/FORWARD as well as for clicks, and
 * `beforeunload` does not cover SPA history navigation, so without this the
 * writer's unsaved text was discarded silently. Extracted as a pure function so
 * the rule is unit-testable rather than only pinned in source.
 *
 * Confirm only when the editor is dirty AND the task actually changes. A
 * re-render, a replace back to the same id, or the initial mount (`from` null)
 * must never prompt — a spurious confirm trains people to dismiss it.
 */
export function shouldConfirmTaskSwitch(args: {
  readonly dirty: boolean;
  readonly fromTaskId: string | null;
  readonly toTaskId: string | null;
}): boolean {
  if (!args.dirty) return false;
  if (args.fromTaskId === null) return false;
  return args.fromTaskId !== args.toTaskId;
}

/**
 * Task 292C1-QA-B — should «مهامي» address this viewer as a WRITER?
 *
 * The page is shared by every production-execute role, and its intro copy said
 * «مهام الإنتاج … وموجز الإنتاج» to everyone, including content writers whose
 * deliverable is text and who have no production brief at all.
 *
 * Role-based, not task-based: an empty queue must still greet a writer as a
 * writer. Designer / video producers keep the production wording verbatim, and
 * a DUAL-HAT (writer who is also a designer or video producer) also keeps it,
 * because that person genuinely sees both kinds of task in this list — mirroring
 * the precedence AppLanding already uses to pick a role home.
 *
 * Pure and exported so the rule is unit-testable rather than only pinned in
 * source (the lesson from 292C1-QA-A).
 */
export function viewerIsWriterOnly(me: {
  readonly roles: ReadonlyArray<{ readonly category: string; readonly key: string }>;
}): boolean {
  const agency = me.roles.filter((r) => r.category === 'agency');
  const has = (key: string): boolean => agency.some((r) => r.key === key);
  if (!has('content_writer')) return false;
  return !has('designer') && !has('video_editor') && !has('videographer');
}

export const MY_TASKS_INTRO_WRITING_AR =
  'مهام الكتابة المُسنَدة إليك. ابدأ بالأقدم، أو افتح المهمة لرؤية تفاصيل المحتوى وموجز الكتابة.';

export const MY_TASKS_INTRO_PRODUCTION_AR =
  'مهام الإنتاج المُسنَدة إليك. ابدأ بالأقدم، أو افتح المهمة لرؤية تفاصيل المحتوى وموجز الإنتاج.';

/** Canonical deep link into the writer workspace for one specific task. */
export const WRITING_WORKSPACE_ROUTE = '/app/me/writing';

export function writingTaskHref(taskId: string): string {
  return `${WRITING_WORKSPACE_ROUTE}/${encodeURIComponent(taskId)}`;
}

/**
 * Task 292C1-QA-A (Codex round 1, P1) — explicit authorization for the new
 * `/app/me/writing[/:taskId]` routes.
 *
 * SECURITY.md line 29 requires every route to pass an explicit permission
 * check. `/app/me/tasks` carries none, but that is a GENERIC queue; this
 * workspace is role-specific, and `AppLanding` only ever routes the authoring
 * `content_writer` to it. A direct URL must not bypass that.
 *
 * Requires BOTH the seeded authoring role and the permission that actually
 * gates the writer RPCs (`task.writing.execute`), so a user whose role or
 * permission was revoked while an assignment lingers is refused at the route.
 * System admins are excluded, matching every other tenant surface.
 *
 * A senior_content_writer who ALSO holds content_writer still passes — the
 * handoff can only assign an active `content_writer`, and AppLanding's
 * precedence (senior review home) is about the LANDING, not about whether a
 * legitimately-assigned dual-hat may open their own task.
 */
export function canOpenWriterWorkspace(me: {
  readonly system_admin?: boolean;
  readonly roles: ReadonlyArray<{ readonly category: string; readonly key: string }>;
  readonly permissions: ReadonlyArray<string>;
}): boolean {
  if (me.system_admin === true) return false;
  const isWriter = me.roles.some((r) => r.category === 'agency' && r.key === 'content_writer');
  if (!isWriter) return false;
  return me.permissions.includes('task.writing.execute');
}

// ── Task 292C2 — the senior writing-review lane ──────────────────────────

export const WRITING_REVIEW_ROUTE = '/app/writing-review';

export function writingReviewHref(draftId: string): string {
  return `${WRITING_REVIEW_ROUTE}/${encodeURIComponent(draftId)}`;
}

/**
 * Task 292C2 — explicit authorization for `/app/writing-review[/:draftId]`.
 *
 * Mirrors `canOpenWriterWorkspace`: role AND the permission that actually
 * gates the RPC, so a revoked permission is refused at the route rather than
 * at the button. `content.writing.approve` is the exact key
 * `review_content_writer_draft_v1` checks, so this FE gate can never be more
 * permissive than the server.
 *
 * THREE roles, not one. The senior content writer is the primary reviewer;
 * owner and marketing_manager are the operational fallback. Before this task
 * the review surface existed only as the `AppLanding` default for
 * `senior_content_writer`, behind `!isOwner && !isMarketingManager && ...` —
 * so the two fallback reviewers were guarded OUT of the only surface that
 * could reach the RPC they are permitted to call.
 *
 * This is presentation only. The server re-derives role, permission, client
 * scope, archived state and self-review on every call.
 */
export function canOpenWritingReview(me: {
  readonly system_admin?: boolean;
  readonly roles: ReadonlyArray<{ readonly category: string; readonly key: string }>;
  readonly permissions: ReadonlyArray<string>;
}): boolean {
  if (me.system_admin === true) return false;
  const agency = (key: string): boolean =>
    me.roles.some((r) => r.category === 'agency' && r.key === key);

  // Task 292C2-A — the ACCOUNT MANAGER tier. The seeded role holds
  // client.manage / content.read / content.schedule / task.assign /
  // publish.schedule and NOT `content.writing.approve`, so gating them on that
  // key would lock them out of a surface the RPC now admits them to. The
  // server binds them to their OWN currently-assigned, non-archived clients;
  // this gate only decides whether the route renders at all.
  if (agency('account_manager')) return true;

  const PERMISSIONED_REVIEWER_ROLES = ['senior_content_writer', 'owner', 'marketing_manager'];
  const isReviewer = me.roles.some(
    (r) => r.category === 'agency' && PERMISSIONED_REVIEWER_ROLES.includes(r.key),
  );
  if (!isReviewer) return false;
  return me.permissions.includes('content.writing.approve');
}

/**
 * Task 292C2-A — an account manager reviews only their OWN clients, so the
 * empty state has to say that rather than the agency-wide wording. Getting this
 * wrong reads as "the queue is broken" when it is really "nothing is waiting on
 * YOUR clients".
 */
export function writingReviewEmptyAr(me: {
  readonly roles: ReadonlyArray<{ readonly category: string; readonly key: string }>;
  readonly permissions: ReadonlyArray<string>;
}): string {
  const isAm = me.roles.some((r) => r.category === 'agency' && r.key === 'account_manager');
  // Codex round-1 P1 — "agency-wide" is leadership AND the permission, matching
  // the server tier exactly. A leadership user whose `content.writing.approve`
  // was revoked is NOT agency-wide: if they also hold the AM role they review
  // only their own clients, so the scoped wording is the honest one for them.
  const isLeadership = me.roles.some(
    (r) => r.category === 'agency' && (r.key === 'owner' || r.key === 'marketing_manager'),
  );
  const isAgencyWide = isLeadership && me.permissions.includes('content.writing.approve');
  if (isAm && !isAgencyWide) return WRITING_REVIEW_EMPTY_SCOPED_AR;
  return WRITING_REVIEW_EMPTY_AR;
}

/** The two decisions this lane offers. Sending to the client is NOT one. */
export type WritingReviewDecision = 'approve_internal' | 'return';

export const WRITING_REVIEW_APPROVE_AR = 'اعتماد المحتوى';
export const WRITING_REVIEW_REQUEST_CHANGES_AR = 'طلب تعديلات';
export const WRITING_REVIEW_TITLE_AR = 'مراجعة الكتابة';
export const WRITING_REVIEW_EMPTY_AR = 'لا توجد مسودات بانتظار المراجعة.';
/** Task 292C2-A — the account-manager wording: scoped to THEIR clients. */
export const WRITING_REVIEW_EMPTY_SCOPED_AR =
  'لا توجد مسودات بانتظار المراجعة على عملائك الحاليين.';
export const WRITING_REVIEW_LOADING_AR = 'جارٍ تحميل قائمة المراجعة…';
export const WRITING_REVIEW_DENIED_AR =
  'لا تملك صلاحية مراجعة مسودات الكتابة. المراجعة متاحة لكبير كتّاب المحتوى، ومالك الوكالة، ومدير التسويق، ومدير الحساب على عملائه.';
export const WRITING_REVIEW_STALE_AR =
  'لم تعد هذه المسودة بانتظار المراجعة. حدِّث القائمة لعرض الحالة الأحدث.';
export const WRITING_REVIEW_ARCHIVED_AR = 'هذا العميل مؤرشف، ولا يمكن اعتماد محتواه أو تعديله.';
export const WRITING_REVIEW_REASON_REQUIRED_AR = 'اكتب سبب طلب التعديلات — سيظهر للكاتب.';
export const WRITING_REVIEW_APPROVED_NOTE_AR =
  'بعد الاعتماد ينتقل المحتوى إلى المراجعة الداخلية. الإرسال للعميل إجراء منفصل.';

/** The reviewer feedback banner on the WRITER's side. */
export const WRITER_FEEDBACK_HEADING_AR = 'ملاحظات المراجعة';

/**
 * Task 292C2 — a request-changes reason is MANDATORY. The RPC refuses a blank
 * one with 23514 (`return requires notes`); this mirrors that rule so the
 * writer never receives an empty rejection, and so the caller is not sent on a
 * round trip that is guaranteed to fail.
 */
export function isValidReviewReason(raw: string): boolean {
  return raw.trim().length > 0;
}

/**
 * Age label for the queue, in Arabic. Deliberately coarse: the reviewer needs
 * "is this stale?" not a timestamp, and a coarse label avoids re-rendering
 * every minute.
 */
export function reviewAgeLabelAr(submittedAt: string | null, now: Date): string {
  if (!submittedAt) return 'غير محدد';
  const then = new Date(submittedAt).getTime();
  if (Number.isNaN(then)) return 'غير محدد';
  const mins = Math.floor((now.getTime() - then) / 60000);
  if (mins < 1) return 'الآن';
  if (mins < 60) return `منذ ${mins} دقيقة`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `منذ ${hours} ساعة`;
  const days = Math.floor(hours / 24);
  return `منذ ${days} يوم`;
}

/**
 * Only a draft still `submitted_to_senior` is actionable. Anything else is a
 * stale selection — someone else reviewed it, or the writer pulled it back —
 * and the surface must say so instead of offering buttons that will 400.
 */
export function isReviewable(status: string | null | undefined): boolean {
  return status === 'submitted_to_senior';
}

/** Read `discipline` off the untyped assignment metadata passthrough. */
export function assignmentDiscipline(task: MyProductionTask): string | null {
  const raw = task.assignment_metadata as Record<string, unknown> | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  const d = raw['discipline'];
  return typeof d === 'string' ? d : null;
}

/** Group the caller's drafts by their parent content_item (one fetch covers all). */
export function draftsByContentItem(
  drafts: ReadonlyArray<ContentWriterDraftRow>,
): ReadonlyMap<string, ReadonlyArray<ContentWriterDraftRow>> {
  const m = new Map<string, ContentWriterDraftRow[]>();
  for (const d of drafts) {
    const arr = m.get(d.content_item_id);
    if (arr) arr.push(d);
    else m.set(d.content_item_id, [d]);
  }
  return m;
}

/**
 * The lane for a content task, from its task status + its variants' draft
 * statuses. Precedence is most-actionable-first so the writer always sees the
 * work that needs them now: blocked → returned → drafting → submitted →
 * approved (all drafts approved) → not_started (no draft yet).
 */
export function deriveContentLane(
  taskStatus: MyProductionTaskStatus,
  draftStatuses: ReadonlyArray<ContentWriterDraftStatus>,
): ContentLaneKey {
  if (taskStatus === 'blocked') return 'blocked';
  if (draftStatuses.includes('returned_with_notes')) return 'returned';
  if (draftStatuses.includes('drafting')) return 'drafting';
  if (draftStatuses.includes('submitted_to_senior')) return 'submitted';
  if (draftStatuses.length > 0 && draftStatuses.every((s) => s === 'approved')) return 'approved';
  return 'not_started';
}

export interface ContentTaskLane {
  readonly key: ContentLaneKey;
  readonly label: string;
  readonly deemphasized: boolean;
  readonly tasks: ReadonlyArray<MyProductionTask>;
}

/** Bucket content tasks into the draft-state lanes (empty lanes preserved). */
export function groupContentTasksByLane(
  tasks: ReadonlyArray<MyProductionTask>,
  drafts: ReadonlyArray<ContentWriterDraftRow>,
): ReadonlyArray<ContentTaskLane> {
  const byItem = draftsByContentItem(drafts);
  const laneOf = (t: MyProductionTask): ContentLaneKey => {
    const itemId = t.content_item?.id;
    const ds = (itemId ? (byItem.get(itemId) ?? []) : []).map((d) => d.status);
    return deriveContentLane(t.task_status, ds);
  };
  return CONTENT_LANES.map((lane) => ({
    key: lane.key,
    label: lane.label,
    deemphasized: lane.deemphasized,
    tasks: tasks.filter((t) => laneOf(t) === lane.key),
  }));
}

// ── Editable writing fields ──────────────────────────────────────────
export interface WriterField {
  readonly key: string;
  readonly label: string;
  readonly multiline: boolean;
}
/** Top-level content_variant text columns the writer edits. */
export const WRITER_TEXT_FIELDS: ReadonlyArray<WriterField> = [
  { key: 'headline', label: 'العنوان / الهيدلاين', multiline: false },
  { key: 'caption', label: 'الكابشن', multiline: true },
  { key: 'body', label: 'النص / متن المنشور', multiline: true },
];
/** platform_specific keys the writer edits for every content variant. */
export const WRITER_PS_FIELDS: ReadonlyArray<WriterField> = [
  { key: 'hook', label: 'الخطّاف (Hook)', multiline: false },
  { key: 'on_image_text', label: 'نص على الصورة', multiline: true },
  { key: 'cta', label: 'الدعوة للإجراء (CTA)', multiline: false },
  { key: 'hashtags', label: 'الوسوم (hashtags)', multiline: true },
];
/** Additional platform_specific keys shown only for VIDEO platforms. */
export const WRITER_VIDEO_PS_FIELDS: ReadonlyArray<WriterField> = [
  { key: 'script', label: 'السكربت', multiline: true },
  { key: 'shot_list', label: 'قائمة اللقطات', multiline: true },
  { key: 'on_screen_text', label: 'النصوص على الشاشة', multiline: true },
  { key: 'audio_direction', label: 'توجيه الصوت / الفويس أوفر', multiline: true },
];

/** The platform_specific writer fields for a given platform (video adds more). */
export function writerPsFields(
  platform: ContentVariantPlatform | null,
): ReadonlyArray<WriterField> {
  if (platform && isVideoBriefPlatform(platform)) {
    return [...WRITER_PS_FIELDS, ...WRITER_VIDEO_PS_FIELDS];
  }
  return WRITER_PS_FIELDS;
}

/** Read a platform_specific value as an editable string (arrays join with spaces). */
export function psFieldToString(
  ps: Readonly<Record<string, unknown>> | null | undefined,
  key: string,
): string {
  if (!ps) return '';
  const raw = ps[key];
  if (raw === null || raw === undefined) return '';
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === 'string').join(' ');
  }
  return typeof raw === 'string' ? raw : String(raw);
}

/** Merge edited platform_specific string fields back over the existing object
 *  (empty string clears the key). Never mutates the input. */
export function buildPlatformSpecific(
  existing: Readonly<Record<string, unknown>> | null | undefined,
  edited: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(existing ?? {}) };
  for (const [k, v] of Object.entries(edited)) {
    const trimmed = v.trim();
    if (trimmed.length === 0) delete out[k];
    else out[k] = trimmed;
  }
  return out;
}

// ── Senior review: send-to-client eligibility (mirrors the backend gate) ──
/**
 * Whether the senior's "approve and send to client" is allowed for a content
 * item of this origin. The Task 220 backend reuses `transition_content_item_
 * status_v2`'s `draft → pending_client_content_approval` edge, which only
 * accepts `manual` / `imported` origin; AI items must go through the
 * owner/MM/AM internal-review route, so the senior cannot send them directly.
 */
export function canSendToClient(origin: ContentOrigin | null | undefined): boolean {
  return origin === 'manual' || origin === 'imported';
}
export const SEND_TO_CLIENT_AI_BLOCKED_AR =
  'محتوى مُولّد/مدعوم بالذكاء الاصطناعي — لا يمكن إرساله مباشرةً للعميل من هنا؛ يمرّ عبر المراجعة الداخلية لدى المالك / مدير التسويق / مدير الحسابات.';

/** Resolve the selected row by id, falling back to the first. Null when empty. */
export function resolveSelected<T extends { readonly id: string }>(
  rows: ReadonlyArray<T>,
  selectedId: string | null,
): T | null {
  if (rows.length === 0) return null;
  if (selectedId) {
    const found = rows.find((r) => r.id === selectedId);
    if (found) return found;
  }
  return rows[0] ?? null;
}

// ── Task 292B2 — the fields senior approval can PROMOTE ──────────────────
/**
 * The writer-owned `platform_specific` keys that
 * `review_content_writer_draft_v1` merges into the canonical variant on
 * approval (migration 20260827120000). This array is the FRONTEND MIRROR of
 * that SQL allowlist: if the migration's key list changes, change this too —
 * a senior must never approve a field they were not shown.
 *
 * There is deliberately NO platform filter here. The SQL loop promotes all
 * eight keys regardless of platform, so filtering the preview by platform
 * would let a static-platform draft carrying `script` (or any other
 * video key) change canonical data the reviewer never saw. Rows that are
 * empty on both sides are dropped instead — that keeps the table short
 * without ever hiding a write.
 */
export interface PromotableBriefField {
  readonly key: string;
  readonly label: string;
}

export const WRITER_PROMOTABLE_FIELDS: ReadonlyArray<PromotableBriefField> = [
  { key: 'hook', label: BRIEF_LABELS_AR.hook },
  { key: 'on_image_text', label: BRIEF_LABELS_AR.onImageText },
  { key: 'cta', label: BRIEF_LABELS_AR.cta },
  { key: 'hashtags', label: BRIEF_LABELS_AR.hashtags },
  { key: 'script', label: BRIEF_LABELS_AR.script },
  { key: 'shot_list', label: BRIEF_LABELS_AR.shotList },
  { key: 'on_screen_text', label: BRIEF_LABELS_AR.onScreenText },
  { key: 'audio_direction', label: BRIEF_LABELS_AR.audioDirection },
];

/**
 * What approval will DO to a canonical key — mirrors the SQL merge exactly:
 *   - `preserved` — the draft does not carry the key (`v_d_ps ? key` false),
 *     so the canonical value stays. This is NOT a clear.
 *   - `cleared`   — the draft carries explicit JSON `null`, or `hashtags: []`.
 *   - `set`       — the draft carries a value, which replaces the canonical one.
 */
export type PromoteEffect = 'preserved' | 'cleared' | 'set';

export interface PromotableComparisonRow {
  readonly key: string;
  readonly label: string;
  /** Current canonical value, formatted; null when the variant has none. */
  readonly canonical: string | null;
  /** Submitted value, formatted; null when the key is cleared or preserved. */
  readonly draft: string | null;
  readonly effect: PromoteEffect;
  /** True when approval would CHANGE the canonical value. */
  readonly changed: boolean;
}

/** True when the draft object actually carries the key (presence, not value). */
function draftHasKey(
  draftPs: ContentVariantPlatformSpecific | null | undefined,
  key: string,
): boolean {
  return !!draftPs && Object.prototype.hasOwnProperty.call(draftPs, key);
}

/**
 * Render a raw `platform_specific` value EXACTLY as it will be stored.
 *
 * `readBriefField` is a DISPLAY formatter: it trims, drops non-string array
 * entries and coerces with `String()`. That is fine for a read-only brief, but
 * a promote preview must not be lossy — `"x"` vs `" x "`, or `["#a"]` vs
 * `["#a", 7]`, are DIFFERENT writes that would otherwise render identically.
 * Strings are shown verbatim; everything else as JSON, so arrays, numbers and
 * objects are unambiguous.
 */
function renderRawValue(raw: unknown): string {
  return typeof raw === 'string' ? raw : JSON.stringify(raw);
}

/** Stable JSON identity of a raw value (`undefined` means the key is absent). */
function rawIdentity(raw: unknown): string {
  return raw === undefined ? ' absent' : JSON.stringify(raw);
}

/** Read a raw key off a platform_specific object without formatting it. */
function rawValue(ps: ContentVariantPlatformSpecific | null | undefined, key: string): unknown {
  if (!ps || !Object.prototype.hasOwnProperty.call(ps, key)) return undefined;
  return (ps as Record<string, unknown>)[key];
}

/**
 * Build the senior's promote-preview: every field approval can write, with the
 * current canonical value beside the submitted one, and an explicit statement
 * of what approval will do to it.
 *
 * A row is dropped only when the key is absent from the draft AND empty on the
 * canonical side — i.e. when approval provably does nothing to it. A field the
 * writer CLEARS is always shown, so the senior sees the removal before
 * approving it.
 */
export function promotableComparison(
  canonicalPs: ContentVariantPlatformSpecific | null | undefined,
  draftPs: ContentVariantPlatformSpecific | null | undefined,
): ReadonlyArray<PromotableComparisonRow> {
  const rows: PromotableComparisonRow[] = [];
  for (const f of WRITER_PROMOTABLE_FIELDS) {
    const canonicalRaw = rawValue(canonicalPs, f.key);
    // Faithful when a value is present; the friendly formatter is used only to
    // decide "is there anything here at all".
    const canonical =
      canonicalRaw === undefined || canonicalRaw === null
        ? readBriefField(canonicalPs, f.key)
        : renderRawValue(canonicalRaw);

    if (!draftHasKey(draftPs, f.key)) {
      // SQL preserves the canonical value; show it only if there is one.
      if (canonical === null) continue;
      rows.push({
        key: f.key,
        label: f.label,
        canonical,
        draft: null,
        effect: 'preserved',
        changed: false,
      });
      continue;
    }

    const raw = rawValue(draftPs, f.key);
    const clears = raw === null || (f.key === 'hashtags' && Array.isArray(raw) && raw.length === 0);
    if (clears) {
      rows.push({
        key: f.key,
        label: f.label,
        canonical,
        draft: null,
        effect: 'cleared',
        changed: canonicalRaw !== undefined,
      });
      continue;
    }

    // A present, non-clearing value. `changed` is decided on the RAW JSON, not
    // on the formatted display string, so a whitespace-only edit or a
    // non-string array element can never be reported as "unchanged".
    rows.push({
      key: f.key,
      label: f.label,
      canonical,
      draft: renderRawValue(raw),
      effect: 'set',
      changed: rawIdentity(raw) !== rawIdentity(canonicalRaw),
    });
  }
  return rows;
}

// ── Task 292B3 — editor dirty-state integrity ────────────────────────────
/**
 * The writer's four editor buffers, as held in the workspace form.
 *
 * `ps` is the flattened string form of the writer-editable `platform_specific`
 * fields (that is what the inputs bind to); the RAW jsonb is rebuilt at save /
 * submit time by `buildPlatformSpecific`, so nothing here is lossy on the wire.
 */
/**
 * Build the `platform_specific` to send, WITHOUT losing raw JSON.
 *
 * The editor binds to flattened STRINGS (that is what a text input produces),
 * and `psFieldToString` is lossy on the way in: it space-joins arrays and
 * `String()`s anything else. Rebuilding the whole object from those strings
 * would therefore rewrite an untouched array as a string, mangle numbers and
 * objects, and drop keys the editor does not display.
 *
 * So: start from the RAW source object, and overwrite ONLY the keys the writer
 * actually changed (edited value differs from the value hydrated into the
 * baseline). Untouched keys — displayed or not, string or not — pass through
 * byte-identical.
 */
export function buildPlatformSpecificFromEdits(
  rawSource: Readonly<Record<string, unknown>> | null | undefined,
  baselinePs: Readonly<Record<string, string>>,
  editedPs: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(rawSource ?? {}) };
  for (const [k, v] of Object.entries(editedPs)) {
    if ((baselinePs[k] ?? '') === (v ?? '')) continue; // untouched -> keep RAW
    // A CHANGED value is written VERBATIM. Trimming here would silently alter
    // what the writer typed (`"  new  "` -> `"new"`), which contradicts both
    // "submit exactly what is on screen" and the raw-fidelity contract. An
    // EMPTY string is the one exception: that is the writer clearing a field.
    if (v === '') delete out[k];
    else out[k] = v;
  }
  return out;
}

export interface WriterEditorBuffers {
  readonly headline: string;
  readonly caption: string;
  readonly body: string;
  readonly ps: Readonly<Record<string, string>>;
}

/**
 * Identity of "what is loaded in the editor" = the SELECTED VARIANT, and
 * nothing else. A change here is a genuine user selection change and is the
 * only situation in which hydrating over the buffers is correct without
 * further checks.
 *
 * Deliberately EXCLUDES the draft id, the draft status and `updated_at`. All
 * three move as a RESULT of the writer's own actions — the first Save creates
 * the draft (null id → an id) and a `returned_with_notes` draft flips to
 * `drafting` — and the refetch that follows would then look like a selection
 * change and overwrite anything typed since the request was sent. The content
 * is the same editor either way, so only the variant identifies it.
 */
export function editorIdentityKey(selectedVariantId: string | null | undefined): string {
  return selectedVariantId ?? '-';
}

/** True when the buffers differ from the baseline they were hydrated from. */
export function isEditorDirty(
  buffers: WriterEditorBuffers,
  baseline: WriterEditorBuffers | null,
): boolean {
  if (!baseline) return false;
  if (
    buffers.headline !== baseline.headline ||
    buffers.caption !== baseline.caption ||
    buffers.body !== baseline.body
  ) {
    return true;
  }
  const keys = new Set([...Object.keys(buffers.ps), ...Object.keys(baseline.ps)]);
  for (const k of keys) {
    if ((buffers.ps[k] ?? '') !== (baseline.ps[k] ?? '')) return true;
  }
  return false;
}

/**
 * The single rule that decides whether incoming server data may overwrite the
 * editor.
 *
 * Before Task 292B3 the prefill effect ran unconditionally on every refetch, so
 * a background reload silently destroyed whatever the writer had typed. The
 * order below is the contract:
 *
 *   1. no baseline yet  → initial load, hydrate;
 *   2. identity changed → a genuine variant/draft change, hydrate;
 *   3. dirty            → NEVER hydrate (typed work always wins);
 *   4. otherwise        → hydrate only if the server revision actually moved.
 */
export function shouldHydrateEditor(args: {
  readonly hasBaseline: boolean;
  readonly identityChanged: boolean;
  readonly dirty: boolean;
  readonly serverRevisionChanged: boolean;
  /**
   * The canonical `content_variants.updated_at` moved since the baseline was
   * taken. This is SEPARATE from `serverRevisionChanged` (which compares the
   * displayed buffers): a canonical edit can change only fields the writer does
   * not see, leaving the buffers identical. If that did not re-baseline, the
   * declared base would stay stale forever and a perfectly legitimate submit
   * would get a spurious 409.
   */
  readonly canonicalRevisionChanged?: boolean;
}): boolean {
  if (!args.hasBaseline) return true;
  if (args.identityChanged) return true;
  if (args.dirty) return false;
  return args.serverRevisionChanged || args.canonicalRevisionChanged === true;
}

/** Arabic copy for the unsaved-changes affordances (RTL surfaces). */
export const UNSAVED_BADGE_AR = 'تغييرات غير محفوظة';
export const UNSAVED_LEAVE_CONFIRM_AR =
  'لديك تغييرات غير محفوظة في هذه المسودّة. إذا تابعت ستفقدها. هل تريد المتابعة؟';
export const SUBMIT_CONFLICT_AR =
  'تغيّر المحتوى الأساسي بعد أن بدأت الكتابة. نصّك المكتوب لم يُفقد. راجع التغييرات، ثم اضغط «إرسال لكبير الكتّاب» مرّة أخرى لإرساله فوق النسخة المحدَّثة.';

/**
 * Task 292C1-QA-A — resolve by **task_id**, and never substitute a different
 * task for one that was explicitly asked for.
 *
 * Two defects are fixed here. It used to match `assignment_id`, which is NOT
 * stable: `send_content_item_to_writing_v1` ends the old assignment row and
 * inserts a new one on reassignment, so the id changed while the task did
 * not. And on a miss it returned `tasks[0]` — list order — so an unknown or
 * inaccessible id silently surfaced somebody else's task.
 *
 * The queue is already RLS-scoped to the caller's own assignments, so "not in
 * this list" means "not yours (or not a writing task)". Returning null lets
 * the caller render an explicit not-found panel rather than leaking whatever
 * happened to be first.
 */
export function resolveWritingTaskById(
  tasks: ReadonlyArray<MyProductionTask>,
  id: string | null,
): MyProductionTask | null {
  if (tasks.length === 0) return null;
  if (id) return tasks.find((t) => t.task_id === id) ?? null;
  return tasks[0] ?? null;
}

// ── Task 292C3 — writer AI generation ────────────────────────────────

/**
 * Task 292C3 — presentation gate for the writer's AI generation panel.
 *
 * Mirrors `canOpenWriterWorkspace` exactly. The server re-derives every
 * predicate (agency role, `task.writing.execute`, live non-archived
 * assignment, active client status) via
 * `app.require_writer_ai_calendar_scope` inside the save RPC AND the
 * generate EF's preflight, so this gate ONLY decides whether the panel
 * renders. It CANNOT grant an authorization the server refuses.
 */
export function canOpenWriterAiGeneration(me: {
  readonly system_admin?: boolean;
  readonly roles: ReadonlyArray<{ readonly category: string; readonly key: string }>;
  readonly permissions: ReadonlyArray<string>;
}): boolean {
  return canOpenWriterWorkspace(me);
}

/**
 * Task 292C3 — filter the caller's `me.client_assignments` to the set
 * that the writer AI generation surface may pick from: `client_status`
 * strictly `'active'` (SECURITY.md §2.1 blocks paused / onboarding /
 * archived from AI runs).
 *
 * The `me` endpoint already excludes ended assignments, so this filter
 * only has to sort on client status. The server re-derives everything —
 * a stale FE cache showing an `active` assignment for a paused client
 * still fails at the RPC.
 */
export function activeAssignedClientsForWriter(
  assignments: ReadonlyArray<ClientAssignmentSummary>,
): ReadonlyArray<ClientAssignmentSummary> {
  return assignments
    .filter((a) => a.client_status === 'active')
    .slice()
    .sort((a, b) => a.client_name.localeCompare(b.client_name, 'ar'));
}

export const WRITER_AI_TITLE_AR = 'توليد محتوى بالذكاء الاصطناعي';
export const WRITER_AI_NO_CLIENTS_AR =
  'لا يوجد عملاء نشطون مُسنَدون إليك للتوليد. تواصل مع مدير الحساب لتفعيل عميل أو تعيينك عليه.';
export const WRITER_AI_PICK_CLIENT_AR = 'اختر عميلاً نشطاً مُسنَداً إليك';
export const WRITER_AI_GENERATING_AR = 'جارٍ توليد المسودّة…';
export const WRITER_AI_SAVING_AR = 'جارٍ حفظ المسودّة كمهمّة كتابة…';
export const WRITER_AI_SUCCESS_AR = 'تم إنشاء مهام الكتابة. اختَر إحداها لبدء التحرير.';
export const WRITER_AI_GENERIC_ERROR_AR = 'تعذّر تنفيذ الإجراء. حاول مرّة أخرى.';

/**
 * Task 292C3 — pure builder: turn the AI generation response into the
 * save payload. Kept OUT of the panel component so the mapping is
 * unit-testable and the panel stays a thin presentation layer.
 *
 * Both worker modes are handled:
 *   * `unified`           — one shared caption per idea, fanned across
 *                           every requested platform. Per-platform brief
 *                           entries (`platform_briefs`, Task 108A) are
 *                           projected into each variant's
 *                           `platform_specific`.
 *   * `platform_adapted`  — per-variant caption + optional
 *                           `production_brief`.
 *
 * Every generated item gets one save item; every variant gets its
 * `platform_specific` populated from the model's brief. `scheduled_for`
 * is derived from the item's `scheduled_date` + `scheduled_time_local`
 * as an ISO 8601 timestamp in the caller-supplied `timezone`.
 */
export interface WriterAiSavePayload {
  readonly items: ReadonlyArray<SaveContentCalendarDraftItemInput>;
}

export function buildWriterAiSaveItems(response: GenerateWriterAiDraftsData): WriterAiSavePayload {
  const echo = response.input_echo;
  const tz = echo.timezone;
  if (response.mode === 'unified') {
    const items: SaveContentCalendarDraftItemInput[] = response.draft.items.map((it) => {
      const briefByPlatform = new Map<ContentVariantPlatform, Readonly<Record<string, unknown>>>();
      for (const entry of it.platform_briefs) {
        briefByPlatform.set(entry.platform, sanitizeBrief(entry.brief));
      }
      return {
        title: it.title.trim().slice(0, 200),
        // The unified item's `core_idea` is the operator's context, not
        // published copy. Skip putting it in `body` to keep the item
        // shape simple; the writer edits per-variant text.
        body: null,
        scheduled_for: buildIsoTimestamp(it.scheduled_date, it.scheduled_time_local, tz),
        timezone: tz,
        variants: echo.platforms.map((platform) => ({
          platform,
          headline: null,
          caption: it.caption.trim(),
          body: null,
          platform_specific: buildUnifiedPlatformSpecific(
            it.hashtags,
            briefByPlatform.get(platform),
          ),
        })),
      };
    });
    return { items };
  }
  const items: SaveContentCalendarDraftItemInput[] = response.draft.items.map((it) => ({
    title: it.title.trim().slice(0, 200),
    body: null,
    scheduled_for: buildIsoTimestamp(it.scheduled_date, it.scheduled_time_local, tz),
    timezone: tz,
    variants: it.variants.map((v) => ({
      platform: v.platform,
      headline: v.headline ?? null,
      caption: v.caption,
      body: v.body ?? null,
      platform_specific: buildPlatformSpecificFromAdaptedVariant(v.hashtags, v.production_brief),
    })),
  }));
  return { items };
}

function buildUnifiedPlatformSpecific(
  hashtags: ReadonlyArray<string>,
  brief: Readonly<Record<string, unknown>> | undefined,
): Readonly<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  if (hashtags.length > 0) {
    out['hashtags'] = hashtags.map((t) => t.trim()).filter((t) => t.length > 0);
  }
  if (brief) {
    for (const [k, v] of Object.entries(brief)) {
      if (v === null || v === undefined) continue;
      out[k] = v;
    }
  }
  return out;
}

function buildPlatformSpecificFromAdaptedVariant(
  hashtags: ReadonlyArray<string>,
  brief:
    | {
        readonly on_image_text?: string;
        readonly visual_direction?: string;
        readonly creative_brief?: string;
        readonly asset_format?: string;
        readonly hook?: string;
        readonly script?: string;
        readonly shot_list?: string;
        readonly on_screen_text?: string;
        readonly audio_direction?: string;
        readonly duration_seconds?: number;
      }
    | undefined,
): Readonly<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  if (hashtags.length > 0) {
    out['hashtags'] = hashtags.map((t) => t.trim()).filter((t) => t.length > 0);
  }
  if (brief) {
    for (const [k, v] of Object.entries(brief)) {
      if (v === null || v === undefined) continue;
      out[k] = v;
    }
  }
  return out;
}

function sanitizeBrief(brief: {
  readonly on_image_text?: string;
  readonly visual_direction?: string;
  readonly creative_brief?: string;
  readonly asset_format?: string;
  readonly hook?: string;
  readonly script?: string;
  readonly shot_list?: string;
  readonly on_screen_text?: string;
  readonly audio_direction?: string;
  readonly duration_seconds?: number;
}): Readonly<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(brief)) {
    if (v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Compose `scheduled_date` (YYYY-MM-DD) + `scheduled_time_local`
 * (HH:MM as a WALL-CLOCK time in `timezone`) into an ISO 8601 UTC
 * timestamp string.
 *
 * Codex round 1 (P1) — the earlier implementation appended `Z` and so
 * treated the local time as UTC. For every non-UTC caller (Asia/Riyadh,
 * Asia/Damascus, etc.) that shifted every generated calendar entry by
 * the timezone's offset (09:00 Riyadh saved as 09:00Z → 12:00 Riyadh on
 * display). The fix computes the actual UTC instant that RENDERS as
 * `date T timeLocal` in `timezone`, so the caller's picked slot is what
 * ends up in `content_variants` / `calendar_entries` / `tasks.due_at`.
 *
 * The resolution uses `Intl.DateTimeFormat.formatToParts` — reliable on
 * every modern Node/browser via ICU, no external tz library. The
 * offset is derived from a single formatted probe (KSA / Gulf have no
 * DST; the one-round resolution is exact there). If for some reason
 * the timezone is not a real IANA zone, `Intl.DateTimeFormat` throws,
 * and we surface that as a validation failure.
 */
export function buildIsoTimestamp(date: string, timeLocal: string, timezone: string): string {
  const dateMatch = /^\d{4}-\d{2}-\d{2}$/.exec(date);
  const timeMatch = /^\d{2}:\d{2}(?::\d{2})?$/.exec(timeLocal);
  if (!dateMatch || !timeMatch) {
    throw new Error(`writer-ai: malformed scheduled_date/time_local: ${date} ${timeLocal}`);
  }
  const [yy, mm, dd] = date.split('-').map((n) => Number.parseInt(n, 10));
  const [hh, mi] = timeLocal.split(':').map((n) => Number.parseInt(n, 10));
  if (
    !Number.isFinite(yy) ||
    !Number.isFinite(mm) ||
    !Number.isFinite(dd) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mi)
  ) {
    throw new Error(`writer-ai: malformed scheduled_date/time_local: ${date} ${timeLocal}`);
  }
  // Guess the UTC instant by pretending the wall-clock IS UTC, then
  // measure the timezone's offset at that instant and correct.
  //
  // Codex round 2 (P1) — one round is not enough across a DST boundary:
  // the initial offset comes from the WRONG side of the transition, and
  // subtracting it produces a corrected instant whose actual offset is
  // one hour off. A second measurement at the corrected instant fixes
  // it. For a non-DST zone (KSA, Damascus post-2022, UTC) the two
  // measurements are equal, so the extra iteration is a no-op.
  //
  // "Impossible" spring-forward wall times (e.g. 02:30 America/New_York
  // on the second Sunday of March, which does not exist) still resolve
  // to a well-defined UTC instant — the JS convention — because
  // `Intl.DateTimeFormat` will render our fixed-point candidate as some
  // real wall time and the loop terminates on that.
  const guessMs = Date.UTC(yy!, mm! - 1, dd!, hh!, mi!, 0);
  let offsetMs = timezoneOffsetMs(guessMs, timezone);
  let utcMs = guessMs - offsetMs;
  // Second measurement at the corrected instant — DST convergence.
  const offsetMs2 = timezoneOffsetMs(utcMs, timezone);
  if (offsetMs2 !== offsetMs) {
    offsetMs = offsetMs2;
    utcMs = guessMs - offsetMs;
  }
  // Codex round 3 (P2) — round-trip the resolved UTC back through the
  // target timezone and confirm the wall-clock time matches what the
  // caller requested. A mismatch means the requested wall time does
  // NOT exist in that timezone (spring-forward gap, e.g. 02:30 on the
  // second Sunday of March in America/New_York), and we must NOT
  // silently reschedule to a different slot. Raise so the FE surfaces
  // a validation error instead.
  const rendered = timezoneWallClock(utcMs, timezone);
  if (
    rendered.year !== yy ||
    rendered.month !== mm ||
    rendered.day !== dd ||
    rendered.hour !== hh ||
    rendered.minute !== mi
  ) {
    throw new Error(
      `writer-ai: ${date}T${timeLocal} does not exist in ${timezone} (DST gap or malformed input)`,
    );
  }
  const iso = new Date(utcMs).toISOString();
  if (Number.isNaN(new Date(iso).getTime())) {
    throw new Error(`writer-ai: unresolvable timestamp for zone ${timezone}: ${date} ${timeLocal}`);
  }
  return iso;
}

interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

function timezoneWallClock(utcMs: number, timezone: string): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcMs));
  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = Number.parseInt(p.value, 10);
  }
  return {
    year: map.year ?? 0,
    month: map.month ?? 0,
    day: map.day ?? 0,
    hour: map.hour === 24 ? 0 : (map.hour ?? 0),
    minute: map.minute ?? 0,
  };
}

/**
 * The signed millisecond offset of `timezone` from UTC at `utcMs`.
 * Positive when the zone is ahead of UTC (Asia/Riyadh = +10800000 for
 * UTC+3). Throws on an unknown IANA zone (Intl surfaces
 * `RangeError`).
 */
function timezoneOffsetMs(utcMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcMs));
  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = Number.parseInt(p.value, 10);
  }
  // `hour12: false` renders midnight as `24` on some ICU builds; normalise.
  const hour = map.hour === 24 ? 0 : (map.hour ?? 0);
  const renderedUtcMs = Date.UTC(
    map.year ?? 1970,
    (map.month ?? 1) - 1,
    map.day ?? 1,
    hour,
    map.minute ?? 0,
    map.second ?? 0,
  );
  return renderedUtcMs - utcMs;
}

/**
 * Codex round 1 (P2) — `YYYY-MM` for "now" in a specific timezone. Uses
 * `Intl.DateTimeFormat` so a caller sitting in Asia/Damascus just past
 * midnight picks the current month in Damascus, not in UTC (which is
 * still on the previous day and therefore the previous month around
 * midnight-early-morning UTC).
 */
export function currentMonthYYYYMM(timezone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  let yy = '';
  let mm = '';
  for (const p of parts) {
    if (p.type === 'year') yy = p.value;
    else if (p.type === 'month') mm = p.value;
  }
  if (yy.length !== 4 || mm.length !== 2) {
    // Fall back to UTC only when the intl call somehow returns garbage
    // — safer than silently returning an empty prefix that the picker
    // pattern check would then reject.
    const utcYy = now.getUTCFullYear();
    const utcMm = String(now.getUTCMonth() + 1).padStart(2, '0');
    return `${utcYy}-${utcMm}`;
  }
  return `${yy}-${mm}`;
}
